import { FormEvent, useCallback, useEffect, useState } from "react";
import {
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { auth } from "../../firebase";
import { finalizePendingUecSubmission } from "../../lib/landingPageFinalizers";

const EMAIL_STORAGE_KEY = "emailForUecVerification";

export default function VerifyUecUpdatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(
    () => window.localStorage.getItem(EMAIL_STORAGE_KEY) || "",
  );
  const [needsEmail, setNeedsEmail] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState("");

  const completeVerification = useCallback(
    async (verificationEmail: string) => {
      const normalizedEmail = verificationEmail.trim().toLowerCase();
      if (!normalizedEmail) {
        setNeedsEmail(true);
        setIsProcessing(false);
        return;
      }

      setError("");
      setIsProcessing(true);
      try {
        await signInWithEmailLink(auth, normalizedEmail, window.location.href);
        await finalizePendingUecSubmission(
          searchParams.get("submissionId") || "",
        );
        window.localStorage.removeItem(EMAIL_STORAGE_KEY);
        navigate("/dashboard", { replace: true });
      } catch (verificationError: any) {
        console.error("UEC email-link verification failed:", verificationError);
        setError(
          verificationError?.message ||
            "The verification link is invalid or has expired. Please submit the form again.",
        );
        setIsProcessing(false);
      }
    },
    [navigate, searchParams],
  );

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setError("This is not a valid UEC verification link.");
      setIsProcessing(false);
      return;
    }

    const storedEmail = window.localStorage.getItem(EMAIL_STORAGE_KEY) || "";
    if (!storedEmail) {
      setNeedsEmail(true);
      setIsProcessing(false);
      return;
    }

    void completeVerification(storedEmail);
  }, [completeVerification]);

  const handleEmailConfirmation = (event: FormEvent) => {
    event.preventDefault();
    void completeVerification(email);
  };

  return (
    <main className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <section className="w-full max-w-md rounded-3xl bg-white border border-slate-200 shadow-xl p-8 text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          {isProcessing ? (
            <Loader2 className="h-7 w-7 animate-spin" />
          ) : (
            <MailCheck className="h-7 w-7" />
          )}
        </div>

        <h1 className="text-2xl font-bold text-slate-900">
          Verify your UEC update
        </h1>

        {isProcessing && (
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Verifying your email and applying your latest registration data…
          </p>
        )}

        {needsEmail && !isProcessing && (
          <form className="mt-6 text-left" onSubmit={handleEmailConfirmation}>
            <p className="mb-4 text-sm leading-6 text-slate-600">
              Confirm the email address that received this link.
            </p>
            <label className="block text-sm font-medium text-slate-700">
              Email address
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
              />
            </label>
            <button
              type="submit"
              className="mt-4 w-full rounded-xl bg-amber-500 px-4 py-3 font-semibold text-white hover:bg-amber-600"
            >
              Verify and apply update
            </button>
          </form>
        )}

        {error && (
          <div className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm leading-6 text-red-700">
            {error}
          </div>
        )}
      </section>
    </main>
  );
}
